/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useMenu } from "@app/vue/providers/index";
import {
  extractCategoriesFromServices,
  filterProductsByCategories,
} from "@app/vue/providers/Menu/utils";
import { MenuProductCard } from "@app/vue/pages/ShopMenu/MenuProductCard";
import { MenuProductSelectModal } from "@app/vue/pages/ShopMenu/MenuProductSelectModal";
import { Product, ProductPromotionResult } from "@app/types/index";
import { useLocalization } from "@lemmonhq/uikit";
import type { MenuPdfItem, ShopSetting } from "@lemmonhq/types";

// Helper to get setting value from settings array
const getSettingValue = (
  settings: ShopSetting[] | undefined,
  key: string,
): unknown => {
  if (!settings) return undefined;
  return settings.find((item) => item.key === key)?.value;
};

// Only allow http(s) URLs through — guards against a manager-writable
// setting round-tripping a javascript: (or other dangerous scheme) URL
// that would execute when a guest clicks the rendered link.
export const isHttpUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const parseMenuPdfs = (value: unknown): MenuPdfItem[] => {
  const parseArray = (parsed: unknown): MenuPdfItem[] => {
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      if (
        typeof item !== "object" ||
        item === null ||
        typeof (item as MenuPdfItem).name !== "string" ||
        !isHttpUrl((item as MenuPdfItem).url)
      ) {
        return [];
      }

      const menuPdf = item as MenuPdfItem;
      return [{ name: menuPdf.name, url: menuPdf.url }];
    });
  };

  if (typeof value === "string") {
    if (!value) return [];
    try {
      return parseArray(JSON.parse(value));
    } catch {
      return [];
    }
  }
  if (Array.isArray(value)) {
    return parseArray(value);
  }
  return [];
};

export const MenuPage = defineComponent({
  name: "MenuPage",
  setup() {
    const {
      shop,
      cart,
      allServices,
      allProducts,
      allCategories,
      promotions,
    } = useMenu();
    const { t } = useLocalization();
    const selectedProduct = ref<Product | null>(null);
    const isProductModalOpen = ref(false);

    // The reservation flow never sets a service method, so the provider's
    // `categories`/`products` include staff-only services. Hide them here.
    const publicServices = computed(() =>
      allServices.value.filter((service) => !service.visible_only_to_staff),
    );
    const categories = computed(() =>
      extractCategoriesFromServices(publicServices.value),
    );
    const products = computed(() =>
      filterProductsByCategories(
        allProducts.value,
        categories.value,
        allCategories.value,
      ),
    );

    const menuPdfs = computed(() =>
      parseMenuPdfs(getSettingValue(shop.value?.settings, "menu_pdfs")),
    );

    const handleProductSelect = (product: Product) => {
      return;
      selectedProduct.value = product;
      isProductModalOpen.value = true;
    };

    const handleProductConfirm = (
      product: Product,
      quantity: number,
      promotionResult: ProductPromotionResult,
      selectedBundles?: { bundleId: number; optionIds: number[] }[],
      notes?: string,
    ) => {
      console.log("selectedBundles", selectedBundles);

      const formattedBundles = selectedBundles?.map(
        ({ bundleId, optionIds }) => {
          const bundle = (product!.bundles || []).find(
            (b) => b.id === bundleId,
          )!;
          return {
            bundleId,
            name: bundle.name,
            options: optionIds.map((optionId) => {
              const option = bundle.options.find((o) => o.id === optionId)!;
              return {
                id: option.id,
                title: option.display.title,
                entity_id: option.entity_id,
                entity_type: option.entity_type,
                price: option.price,
              };
            }),
          };
        },
      );
      if (selectedProduct.value) {
        cart.addToCart(
          product,
          formattedBundles,
          undefined,
          quantity,
          notes,
          promotionResult.hasDiscountPromotions ? promotionResult : undefined,
        );
      }
      isProductModalOpen.value = false;
      selectedProduct.value = null;
    };

    return () => (
      <div class="space-y-6">
        {menuPdfs.value.length > 0 ? (
          <div>
            <h3
              class="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4 mt-2 px-1"
              style="letter-spacing: 0.08em;"
            >
              {t("customer.reserve.menu_pdfs_section_title")}
            </h3>
            <div class="space-y-2">
              {menuPdfs.value.map((pdf, index) => (
                <a
                  key={`${pdf.url}-${index}`}
                  href={pdf.url}
                  target="_blank"
                  rel="noreferrer"
                  class="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors"
                >
                  <span class="material-symbols-outlined text-2xl text-gray-500">
                    picture_as_pdf
                  </span>
                  <span class="font-medium text-gray-800">{pdf.name}</span>
                </a>
              ))}
            </div>
          </div>
        ) : null}
        {categories.value.map((category) => {
          const categoryProducts = products.value.filter((product) =>
            category.product_ids.includes(product.id),
          );

          if (categoryProducts.length === 0) return null;

          return (
            <div key={category.id}>
              <h3
                class="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4 mt-2 px-1"
                style="letter-spacing: 0.08em;"
              >
                {category.name}
              </h3>
              <div>
                {categoryProducts.map((product) => (
                  <MenuProductCard
                    key={product.id}
                    product={product}
                    onProductSelect={handleProductSelect}
                    cartItems={cart.cart.items}
                    services={publicServices.value}
                    promotions={promotions.value}
                    disableAdd={true}
                  />
                ))}
              </div>
            </div>
          );
        })}
        {selectedProduct.value && isProductModalOpen.value ? (
          <MenuProductSelectModal
            services={publicServices.value}
            promotions={promotions.value}
            product={selectedProduct.value as any}
            onClose={() => {
              isProductModalOpen.value = false;
              selectedProduct.value = null;
            }}
            onConfirm={handleProductConfirm}
          />
        ) : null}
      </div>
    );
  },
});
